import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Box, TextField, MenuItem, FormControlLabel, Switch, Typography } from '@mui/material';
import { SCUIButton } from '../../../../atomic/atoms';
import StudioPromptCommand from '../../../../services/command/StudioPromptCommand';
import { StudioPrompt, CreateStudioPromptRequest, UpdateStudioPromptRequest } from '../../../../types/StudioPrompt';
import Swal from 'sweetalert2';

interface StudioPromptDrawerProps {
    open: boolean;
    onClose: () => void;
    prompt: StudioPrompt | null;
    onSaveSuccess: () => void;
}

export const StudioPromptDrawer = ({ open, onClose, prompt, onSaveSuccess }: StudioPromptDrawerProps) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        promptText: '',
        promptType: 1,
        category: '',
        isActive: true,
        displayOrder: 0,
    });
    const [errors, setErrors] = useState<any>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (prompt) {
            setFormData({
                name: prompt.name || '',
                promptText: prompt.promptText || '',
                promptType: prompt.promptType || 1,
                category: prompt.category || '',
                isActive: prompt.isActive !== undefined ? prompt.isActive : true,
                displayOrder: prompt.displayOrder || 0,
            });
        } else {
            setFormData({
                name: '',
                promptText: '',
                promptType: 1,
                category: '',
                isActive: true,
                displayOrder: 0,
            });
        }
        setErrors({});
    }, [prompt, open]);

    const handleChange = (field: string, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev: any) => ({ ...prev, [field]: '' }));
        }
    };

    const validate = () => {
        const newErrors: any = {};

        if (!formData.name.trim()) {
            newErrors.name = t('studyoPrompt.nameRequired');
        }

        if (!formData.promptText.trim()) {
            newErrors.promptText = t('studyoPrompt.promptTextRequired');
        }

        if (!formData.category.trim()) {
            newErrors.category = t('studyoPrompt.categoryRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) {
            return;
        }

        try {
            setIsSubmitting(true);
            const username = JSON.parse(localStorage.getItem('checkOperator') || '{}')?.username || 'System';

            if (prompt) {
                // Update
                const request: UpdateStudioPromptRequest = {
                    id: prompt.id,
                    name: formData.name,
                    promptText: formData.promptText,
                    promptType: formData.promptType,
                    category: formData.category,
                    isActive: formData.isActive,
                    displayOrder: formData.displayOrder,
                    updatedBy: username,
                };
                await StudioPromptCommand.UpdateStudioPrompt(prompt.id, request);
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('studyoPrompt.updateSuccess'),
                    timer: 1500,
                    showConfirmButton: false,
                });
            } else {
                // Create
                const request: CreateStudioPromptRequest = {
                    name: formData.name,
                    promptText: formData.promptText,
                    promptType: formData.promptType,
                    category: formData.category,
                    isActive: formData.isActive,
                    displayOrder: formData.displayOrder,
                    createdBy: username,
                };
                await StudioPromptCommand.CreateStudioPrompt(request);
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('studyoPrompt.addSuccess'),
                    timer: 1500,
                    showConfirmButton: false,
                });
            }
            onSaveSuccess();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: 500, padding: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {prompt ? t('studyoPrompt.edit') : t('studyoPrompt.add')}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 3 }}>
                    <TextField
                        label={t('studyoPrompt.name')}
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        error={!!errors.name}
                        helperText={errors.name}
                        placeholder={t('studyoPrompt.placeholders.name')}
                        required
                        fullWidth
                    />

                    <TextField
                        label={t('studyoPrompt.promptText')}
                        value={formData.promptText}
                        onChange={(e) => handleChange('promptText', e.target.value)}
                        error={!!errors.promptText}
                        helperText={errors.promptText}
                        placeholder={t('studyoPrompt.placeholders.promptText')}
                        required
                        fullWidth
                        multiline
                        rows={6}
                    />

                    <TextField
                        select
                        label={t('studyoPrompt.promptType')}
                        value={formData.promptType}
                        onChange={(e) => handleChange('promptType', parseInt(e.target.value))}
                        required
                        fullWidth
                    >
                        <MenuItem value={1}>{t('studyoPrompt.promptTypes.1')}</MenuItem>
                        <MenuItem value={2}>{t('studyoPrompt.promptTypes.2')}</MenuItem>
                        <MenuItem value={3}>{t('studyoPrompt.promptTypes.3')}</MenuItem>
                    </TextField>

                    <TextField
                        label={t('studyoPrompt.category')}
                        value={formData.category}
                        onChange={(e) => handleChange('category', e.target.value)}
                        error={!!errors.category}
                        helperText={errors.category}
                        placeholder={t('studyoPrompt.placeholders.category')}
                        required
                        fullWidth
                    />

                    <TextField
                        label={t('studyoPrompt.displayOrder')}
                        type="number"
                        value={formData.displayOrder}
                        onChange={(e) => handleChange('displayOrder', parseInt(e.target.value) || 0)}
                        placeholder={t('studyoPrompt.placeholders.displayOrder')}
                        fullWidth
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isActive}
                                onChange={(e) => handleChange('isActive', e.target.checked)}
                            />
                        }
                        label={t('studyoPrompt.isActive')}
                    />

                    <Box sx={{ display: 'flex', gap: 2, marginTop: 2 }}>
                        <SCUIButton
                            sizeType="fixed"
                            text={t('general.save')}
                            type="primary"
                            size="m"
                            onClick={handleSave}
                            disabled={isSubmitting}
                            fullwidth
                        />
                        <SCUIButton
                            sizeType="fixed"
                            text={t('general.cancel')}
                            type="secondary"
                            size="m"
                            onClick={onClose}
                            disabled={isSubmitting}
                            fullwidth
                        />
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};
